import React from 'react';
import { Menu, Transition } from '@headlessui/react';
import { marFlag, ukFlag } from '../assets/images/icons';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
import Notify from './useNotify';

const LanguageSelector = ({ screen = 'desktop' }) => {
  const { t } = useTranslation();
  let fr_to_en = "Language set to English";
  let en_to_fr = "Langue définie sur le Français";
  const [currentLang, setCurrentLang] = React.useState(() => {
    return localStorage.getItem("language") || "fr";
  });

  const languages = [
    { code: 'fr', name: 'french', flag: marFlag },
    { code: 'en', name: 'english', flag: ukFlag }
  ];

  React.useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, [currentLang]);

  const switchLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLang(lang);
    localStorage.setItem("language", lang);
    Notify('success', lang === "fr" ? en_to_fr : fr_to_en);
  };

  if (screen === 'mobile') {
    return (
      <div className="border-t border-gray-200 px-4 py-6">
        <Menu as="div" className="relative">
          <Menu.Button className="-m-2 flex items-center p-2">
            <img loading="lazy"
              src={languages.find(lang => lang.code === currentLang)?.flag}
              alt={currentLang}
              className="block h-auto w-6 shrink-0"
            />
            <span className="ml-2.5 block text-base font-medium text-gray-900">
              {currentLang === "fr" ? "MA" : "UK"}
            </span>
            <ChevronDownIcon className="ml-1 h-4 w-4" />
          </Menu.Button>

          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute left-0 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {languages.map((lang) => (
                <Menu.Item key={lang.code}>
                  {({ active }) => (
                    <button
                      onClick={() => switchLanguage(lang.code)}
                      className={`${active ? 'bg-gray-100' : ''
                        } group flex w-full items-center px-4 py-2 text-sm`}
                    >
                      <img loading="lazy" src={lang.flag} alt={lang.code} className="mr-2.5 h-auto w-6" />
                      {t(lang.name)}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    );
  } else if (screen === 'desktop') {
    return (
      <div className="hidden lg:ml-8 lg:flex z-30">
        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center text-gray-700 hover:text-gray-800">
            <img loading="lazy"
              src={languages.find(lang => lang.code === currentLang)?.flag}
              alt={currentLang}
              className="block h-auto w-6 shrink-0"
            />
            <span className="ml-2.5 block text-sm font-medium">
              {currentLang === "fr" ? "MA" : "UK"}
            </span>
            <ChevronDownIcon className="ml-1 h-4 w-4" />
          </Menu.Button>

          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {languages.map((lang) => (
                <Menu.Item key={lang.code}>
                  {({ active }) => (
                    <button
                      onClick={() => switchLanguage(lang.code)}
                      className={`${active ? 'bg-gray-100' : ''
                        } group flex w-full items-center px-4 py-2 text-sm`}
                    >
                      <img loading="lazy" src={lang.flag} alt={lang.code} className="mr-2.5 h-auto w-6" />
                      {t(lang.name)}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    );
  } else if (screen === 'dashboard-desktop') {
    return (
      <div className="ml-8 flex z-30">
        <Menu as="div" className="relative">
          <Menu.Button className="capitalize flex items-center gap-2 max-md:gap-0 py-2 text-sm font-medium text-black hover:text-black/80">
            {/* <img loading="lazy"
              src={languages.find(lang => lang.code === currentLang)?.flag}
              alt={currentLang}
              className="block h-auto w-6 shrink-0"
            />
            <span className="ml-2.5 block text-sm font-medium">
              {currentLang === "fr" ? "MA" : "UK"}
            </span> */}

            <div className="uppercase size-8 flex justify-center items-center text-sm text-black border border-black font-semibold rounded-full">
              {currentLang}
            </div>
            <div className="flex-col text-start max-md:hidden">
              <h1 className="text-sm leading font-semibold">{currentLang === "fr" ? t("french") : t("english")}</h1>
            </div>
            <div className="border border-black/50 rounded-full ml-2 p-0.5">
              <ChevronDownIcon className="h-4 w-4" />
            </div>
          </Menu.Button>

          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {languages.map((lang) => (
                <Menu.Item key={lang.code}>
                  {({ active }) => (
                    <button
                      onClick={() => switchLanguage(lang.code)}
                      className={`${active ? 'bg-gray-100' : ''
                        } group flex w-full items-center px-4 py-2 text-sm`}
                    >
                      <img loading="lazy" src={lang.flag} alt={lang.code} className="mr-2.5 h-auto w-6" />
                      {t(lang.name)}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    )
  }

};

export default LanguageSelector;